import { useState, useEffect } from 'react';
import type { Plan } from '@calwmp/shared';

interface ComplianceIssue {
  type: 'error' | 'warning' | 'suggestion';
  field: string;
  message: string;
  suggestion?: string;
}

interface DiversionSuggestion {
  materialType: string;
  currentMethod: string;
  currentDiversion: number;
  potentialDiversion: number;
  recommendedMethods: string[];
  bestPractices: string[];
  potentialGain: number;
}

interface ComplianceVerification {
  canSubmit: boolean;
  diversionRate: number;
  diversionShortfall: number;
  issues: ComplianceIssue[];
  suggestions: DiversionSuggestion[];
  jurisdictionCompliant: boolean;
  jurisdictionIssues: ComplianceIssue[];
}

interface ComplianceVerificationPanelProps {
  plan: Plan;
  onVerificationComplete?: (canSubmit: boolean) => void;
}

/**
 * Compliance verification panel component (T113)
 * Shows compliance issues and suggestions for improvement
 */
export function ComplianceVerificationPanel({
  plan,
  onVerificationComplete,
}: ComplianceVerificationPanelProps) {
  const [verification, setVerification] = useState<ComplianceVerification | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verifyCompliance = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/compliance/plans/${plan.id}/verify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to verify compliance');
      }

      const data = await response.json();
      setVerification(data.verification);
      onVerificationComplete?.(data.verification.canSubmit);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify compliance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verifyCompliance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.id]);

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <div className="spinner-lg mb-3" />
          <p className="text-muted">Verifying compliance...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
          <button onClick={verifyCompliance} className="btn btn-primary">
            Retry Verification
          </button>
        </div>
      </div>
    );
  }

  if (!verification) {
    return null;
  }

  const errors = verification.issues.filter((i) => i.type === 'error');
  const warnings = verification.issues.filter((i) => i.type === 'warning');

  return (
    <div className="space-y-4">
      {/* Overall Status */}
      <div className={`card ${verification.canSubmit ? 'border-success' : 'border-error'}`}>
        <div className="card-header">
          <h3 className="card-title">Compliance Status</h3>
        </div>
        <div className="card-body">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-3xl font-bold">
                {verification.diversionRate.toFixed(1)}%
              </div>
              <div className="text-muted">Current Diversion Rate</div>
            </div>
            <div
              className={`badge badge-lg ${verification.canSubmit ? 'badge-success' : 'badge-error'}`}
            >
              {verification.canSubmit ? (
                <>
                  <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Compliant
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Non-Compliant
                </>
              )}
            </div>
          </div>

          {verification.diversionShortfall > 0 && (
            <div className="alert alert-warning">
              You need to increase diversion by{' '}
              <strong>{verification.diversionShortfall.toFixed(1)}%</strong> to meet the
              minimum 65% requirement.
            </div>
          )}
        </div>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="card border-error">
          <div className="card-header bg-error text-white">
            <h4 className="card-title">Errors ({errors.length})</h4>
          </div>
          <div className="card-body">
            <ul className="space-y-2">
              {errors.map((issue, idx) => (
                <li key={idx} className="border-l-4 border-error pl-3">
                  <strong>{issue.message}</strong>
                  {issue.suggestion && (
                    <p className="text-sm text-muted mt-1">{issue.suggestion}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="card border-warning">
          <div className="card-header bg-warning">
            <h4 className="card-title">Warnings ({warnings.length})</h4>
          </div>
          <div className="card-body">
            <ul className="space-y-2">
              {warnings.map((issue, idx) => (
                <li key={idx} className="border-l-4 border-warning pl-3">
                  <strong>{issue.message}</strong>
                  {issue.suggestion && (
                    <p className="text-sm text-muted mt-1">{issue.suggestion}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Improvement Suggestions */}
      {verification.suggestions.length > 0 && (
        <div className="card border-info">
          <div className="card-header bg-info text-white">
            <h4 className="card-title">
              Improvement Suggestions ({verification.suggestions.length})
            </h4>
          </div>
          <div className="card-body">
            <p className="text-muted mb-4">
              These suggestions can help you increase your diversion rate:
            </p>
            <div className="space-y-4">
              {verification.suggestions.map((suggestion, idx) => (
                <div key={idx} className="border rounded p-3">
                  <div className="flex items-center justify-between mb-2">
                    <strong className="text-lg capitalize">{suggestion.materialType}</strong>
                    <span className="badge badge-success">
                      +{suggestion.potentialGain.toFixed(1)}% potential gain
                    </span>
                  </div>
                  <div className="text-sm">
                    <p className="text-muted mb-2">
                      Current: {suggestion.currentMethod} ({suggestion.currentDiversion}%)
                      → Potential: {suggestion.potentialDiversion}%
                    </p>
                    <p className="mb-2">
                      <strong>Recommended:</strong>{' '}
                      {suggestion.recommendedMethods.join(', ')}
                    </p>
                    <details className="mt-2">
                      <summary className="cursor-pointer text-info">
                        View best practices
                      </summary>
                      <ul className="mt-2 ml-4 text-muted">
                        {suggestion.bestPractices.map((practice, pidx) => (
                          <li key={pidx}>{practice}</li>
                        ))}
                      </ul>
                    </details>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Jurisdiction Requirements */}
      {verification.jurisdictionIssues.length > 0 && (
        <div className="card border-warning">
          <div className="card-header bg-warning">
            <h4 className="card-title">Jurisdiction Requirements</h4>
          </div>
          <div className="card-body">
            <ul className="space-y-2">
              {verification.jurisdictionIssues.map((issue, idx) => (
                <li key={idx} className="border-l-4 border-warning pl-3">
                  <strong>{issue.message}</strong>
                  {issue.suggestion && (
                    <p className="text-sm text-muted mt-1">{issue.suggestion}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <button onClick={verifyCompliance} className="btn btn-outline">
        Re-verify Compliance
      </button>
    </div>
  );
}